import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTrailingGlobalRestore } from '@/hooks/useTrailingGlobalRestore';
import { useChinGlobalRestore } from '@/hooks/useChinGlobalRestore';
import { useAutoChinGlobalRestore } from '@/hooks/useAutoChinGlobalRestore';
import { useCopyTradeAutoRestore } from '@/hooks/useCopyTradeAutoRestore';
import { useCtraderOrderAlert } from '@/hooks/useCtraderOrderAlert';

/**
 * EngineBootstrapper
 * ────────────────────────────────────────────────────────────
 * Mounted at the app root (outside the auth route guards) so the trading
 * engines — trailing stop, Chin bot, copy trade, and cTrader order alerts —
 * start as soon as the page loads, even while the user is sitting on the
 * login screen.
 *
 * This decouples the bots from the UI auth gate: logging in is only needed
 * to VIEW / EDIT configuration, not to run the bots. The engines are driven
 * by the persisted Supabase session, so once the user has signed in once
 * (session saved in the browser), the bots auto-start on every page load and
 * keep running until the browser closes or the session is signed out.
 */
export default function EngineBootstrapper() {
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (cancelled) return;
        setUserId(session?.user?.id ?? null);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setUserId(null);
        setLoading(false);
      });

    // React to login/logout so the engines start/stop accordingly.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      setUserId(session?.user?.id ?? null);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useTrailingGlobalRestore(loading ? null : userId);
  useChinGlobalRestore(loading ? null : userId);
  useAutoChinGlobalRestore(loading ? null : userId);
  useCopyTradeAutoRestore(loading ? null : userId);
  useCtraderOrderAlert(loading ? null : userId);

  return null;
}